import type { BundleTreeFile, ModuleManifest } from '@hibro/shared';

import {
  isNotFound,
  listDirectoryNames,
  type OpfsDirectoryHandle,
  opfsRoot,
  readFileAt,
  readJsonAt,
  removeIfPresent,
  writeFileAt,
} from './opfs-tree';

/**
 * Linked (load-unpacked) dev modules in OPFS (见 11 调试 / 18), the browser
 * counterpart of the desktop's `userData/hibro-devtools.json` list of working
 * directories.
 *
 * **Desktop mounts by reference; web keeps a copy, and that difference is
 * forced.** A browser can only reach a local directory through a
 * `FileSystemDirectoryHandle`, and a handle retrieved from IndexedDB usually
 * reports `prompt` until `requestPermission()` is called inside a user gesture —
 * which a Service Worker or a SharedWorker can never provide (measured; the
 * spec says as much). Serving `/_mod/<id>/*` straight off the handle would
 * therefore work only while a persistent grant happened to be in force, and
 * fail as missing files otherwise. So the page — which always has the gesture,
 * because the user just clicked Link or Reload — copies the tree in here, and
 * everything downstream reads OPFS like it does for a delivered module.
 *
 * The cost is that a rebuilt `dist/` needs one more click than on desktop.
 * Front-end iteration is unaffected: a linked module's `views[].url` can point
 * at the author's own dev server (见 12), which no copy sits in front of.
 *
 * No marker and no version here, unlike the bundle cache: a working copy has no
 * content-addressed identity, and Reload replaces it wholesale.
 */

/** OPFS directory holding every linked module's copied tree. */
export const LINKED_MODULES_DIRNAME = 'hibro-linked';

/** Conventional manifest filename at a module root (架构方案 §5.3). */
const MODULE_MANIFEST_FILENAME = 'module.manifest.json';

export interface OpfsLinkedStoreOptions {
  /** Resolve the OPFS root; injected so tests can supply an in-memory tree. */
  getRoot?: () => Promise<OpfsDirectoryHandle>;
}

/** A linked module as found in the store: its id and the manifest read back. */
export interface LinkedModule {
  moduleId: string;
  manifest: ModuleManifest;
}

export class OpfsLinkedStore {
  private readonly getRoot: () => Promise<OpfsDirectoryHandle>;

  constructor(options: OpfsLinkedStoreOptions = {}) {
    this.getRoot = options.getRoot ?? opfsRoot;
  }

  private async dir(create: boolean): Promise<OpfsDirectoryHandle> {
    return (await this.getRoot()).getDirectoryHandle(LINKED_MODULES_DIRNAME, {
      create,
    });
  }

  private async existingDir(): Promise<OpfsDirectoryHandle | undefined> {
    try {
      return await this.dir(false);
    } catch (err) {
      if (isNotFound(err)) {
        return undefined;
      }
      throw err;
    }
  }

  /** Ids of every linked module currently copied in. */
  async list(): Promise<string[]> {
    const dir = await this.existingDir();
    return dir ? listDirectoryNames(dir) : [];
  }

  /**
   * Every linked module whose copy carries a readable manifest with a matching
   * id. A copy that does not is skipped rather than surfaced — the same
   * fail-closed posture the bundle cache applies to a missing marker.
   */
  async read(moduleIds: readonly string[]): Promise<LinkedModule[]> {
    const dir = await this.existingDir();
    if (!dir) {
      return [];
    }
    const found: LinkedModule[] = [];
    for (const moduleId of moduleIds) {
      let root: OpfsDirectoryHandle;
      try {
        root = await dir.getDirectoryHandle(moduleId);
      } catch (err) {
        if (isNotFound(err)) {
          continue;
        }
        throw err;
      }
      const manifest = (await readJsonAt(root, [MODULE_MANIFEST_FILENAME])) as
        | ModuleManifest
        | undefined;
      if (manifest?.id === moduleId) {
        found.push({ moduleId, manifest });
      }
    }
    return found;
  }

  async readFile(
    moduleId: string,
    segments: readonly string[],
  ): Promise<Uint8Array | undefined> {
    const dir = await this.existingDir();
    if (!dir) {
      return undefined;
    }
    try {
      return await readFileAt(await dir.getDirectoryHandle(moduleId), segments);
    } catch (err) {
      if (isNotFound(err)) {
        return undefined;
      }
      throw err;
    }
  }

  /**
   * Replace a linked module's copy with `entries`, returning the manifest read
   * back out of what was written. Clearing first is what makes Reload mean
   * "this is the directory now" rather than "merge onto whatever was here".
   */
  async write(
    moduleId: string,
    entries: readonly BundleTreeFile[],
  ): Promise<ModuleManifest | undefined> {
    const dir = await this.dir(true);
    await removeIfPresent(dir, moduleId);
    const root = await dir.getDirectoryHandle(moduleId, { create: true });
    for (const entry of entries) {
      await writeFileAt(root, entry.segments, entry.data);
    }
    return (await readJsonAt(root, [MODULE_MANIFEST_FILENAME])) as
      | ModuleManifest
      | undefined;
  }

  async remove(moduleId: string): Promise<void> {
    const dir = await this.existingDir();
    if (dir) {
      await removeIfPresent(dir, moduleId);
    }
  }
}
